"use client";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useCallback, useMemo, useEffect, useRef, useLayoutEffect, forwardRef, useImperativeHandle, } from "react";
import { useReactTable, getCoreRowModel, getSortedRowModel, flexRender, } from "@tanstack/react-table";
// eslint-disable-next-line import/no-extraneous-dependencies
import { useVirtualizer, useWindowVirtualizer, } from "@tanstack/react-virtual";
import { Table, Thead, Tbody, TableContainer, Tr, Th, Td, Flex, Text, useMultiStyleConfig, Box, } from "@chakra-ui/react";
import { SvgSpriteConsumer, Illustration, SkeletonTypes, Skeleton, } from "../../atoms";
import { paddingTopCalc, paddingBottomCalc, getSortIcon, selectionColumn, } from "./helpers";
import { Illustrations } from "../../atoms/Illustration";
import { PROD_CDN_BASE_URL, KeyboardCodes } from "../../helpers";
function getPinColumnBoxShadow(isLastLeftPinnedColumn, isFirstRightPinnedColumn, colIndex, isFocused) {
    const baseShadowLeftPin = "-1px 0 0 0 var(--chakra-colors-semantic-border-secondary) inset";
    const baseShadowRightPin = "1px 0 0 0 var(--chakra-colors-semantic-border-secondary) inset";
    const focusShadow = "inset 4px 0 0 0 var(--chakra-colors-semantic-interactive-primary)";
    if (isLastLeftPinnedColumn) {
        if (colIndex === 0 && isFocused) {
            return `${baseShadowLeftPin}, ${focusShadow}`;
        }
        return baseShadowLeftPin;
    }
    if (isFirstRightPinnedColumn) {
        return baseShadowRightPin;
    }
    return colIndex === 0 && isFocused ? focusShadow : undefined;
}
const getCommonPinningStyles = (column, isFocused) => {
    const isPinned = column.getIsPinned();
    const isLastLeftPinnedColumn = isPinned === "left" && column.getIsLastColumn("left");
    const isFirstRightPinnedColumn = isPinned === "right" && column.getIsFirstColumn("right");
    return isPinned
        ? {
            boxShadow: getPinColumnBoxShadow(isLastLeftPinnedColumn, isFirstRightPinnedColumn, column.getIndex(), isFocused),
            left: isPinned === "left" ? `${column.getStart("left")}px` : undefined,
            right: isPinned === "right" ? `${column.getAfter("right")}px` : undefined,
            opacity: isPinned ? 0.95 : 1,
            position: isPinned ? "sticky" : "relative",
            width: column.getSize(),
            zIndex: isPinned ? 1 : 0,
        }
        : {};
};
const CustomCell = ({ cell, isFocused, }) => {
    const styles = useMultiStyleConfig("Table", { variant: "infinite" });
    const { id, getSize, columnDef } = cell.column;
    const width = getSize() === Number.MAX_SAFE_INTEGER ? "auto" : `${getSize()}px`;
    return (_jsx(Td, Object.assign({ sx: styles.td, width: width, whiteSpace: "normal", style: Object.assign({}, getCommonPinningStyles(cell.column, isFocused)) }, { children: _jsx(Flex, Object.assign({ display: "flex", alignItems: "center", 
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            justifyContent: columnDef.align }, { children: flexRender(columnDef.cell, cell.getContext()) })) }), id));
};
const CustomTr = ({ padding }) => padding > 0 ? (_jsx(Tr, { children: _jsx(Td, { height: `${padding}px` }) })) : null;
const InfiniteDataTable = ({ columns, data, enableRowSelection, enableSorting, onRowSelectionChange, noResultLabel = "No result found", initialSorting = [], overscan = 5, tableHeight = "500px", // to be adjusted once windowVirtualizer added
tableMaxHeight, onScroll, windowScroll = true, onManualSort, onClickRow, rowClickLabel = "View", hasNextPage = false, skeletonRows = 0, isFocused, setColumnVisibility, columnVisibility, setColumnOrder, columnOrder, noResultIllustrationId = Illustrations.NOT_FOUND, }, ref) => {
    const styles = useMultiStyleConfig("Table", {
        variant: "infinite",
        windowScroll,
    });
    const flagFirstRenderRef = useRef(false);
    const tableContainerRef = useRef(null);
    const parentOffsetRef = useRef(0);
    const [rowSelection, setRowSelection] = useState({});
    const [sorting, setSorting] = useState(initialSorting);
    const columnsToDisplay = useMemo(() => {
        const columnsTemp = [...columns];
        if (enableRowSelection) {
            columnsTemp.unshift(selectionColumn);
        }
        return columnsTemp;
    }, [columns, enableRowSelection]);
    // columns in tanstack data have always size defined. To know if we are in "auto" default case or not at display time, we override default value with Number.MAX_SAFE_INTEGER constant.
    const [columnPinning, setColumnPinning] = React.useState({
        left: columnsToDisplay
            // @ts-expect-error no type
            .filter((col) => col.sticky === "left")
            // @ts-expect-error no type
            .map((col) => col.id),
        right: columnsToDisplay
            // @ts-expect-error no type
            .filter((col) => col.sticky === "right")
            // @ts-expect-error no type
            .map((col) => col.id),
    });
    useLayoutEffect(() => {
        var _a, _b;
        if (windowScroll) {
            parentOffsetRef.current = (_b = (_a = tableContainerRef.current) === null || _a === void 0 ? void 0 : _a.offsetTop) !== null && _b !== void 0 ? _b : 0;
        }
    }, [windowScroll]);
    const getItemKey = useCallback((index) => (data === null || data === void 0 ? void 0 : data[index]) ? `${index}-${data[index].id}` : `skeleton-${index}`, [data]);
    const table = useReactTable({
        data,
        columns: columnsToDisplay,
        defaultColumn: {
            minSize: 0,
            size: Number.MAX_SAFE_INTEGER,
            maxSize: Number.MAX_SAFE_INTEGER,
        },
        state: {
            rowSelection,
            sorting,
            columnPinning,
            columnVisibility,
            columnOrder,
        },
        onColumnVisibilityChange: setColumnVisibility,
        onColumnOrderChange: setColumnOrder,
        onColumnPinningChange: setColumnPinning,
        enableRowSelection,
        enableSorting,
        manualSorting: !!onManualSort,
        enableMultiRowSelection: enableRowSelection,
        onSortingChange: setSorting,
        onRowSelectionChange: setRowSelection,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });
    useImperativeHandle(ref, () => ({
        table,
    }));
    useEffect(() => {
        if (onManualSort) {
            onManualSort(sorting);
        }
    }, [onManualSort, sorting]);
    const windowVirtualizer = useWindowVirtualizer({
        count: hasNextPage ? data.length + skeletonRows : data.length,
        scrollMargin: parentOffsetRef.current,
        estimateSize: () => 40,
        overscan,
        getItemKey,
    });
    const virtualizer = useVirtualizer({
        count: hasNextPage ? data.length + skeletonRows : data.length,
        getScrollElement: () => tableContainerRef.current,
        estimateSize: () => 40,
        overscan,
        getItemKey,
    });
    const { getVirtualItems, getTotalSize, measureElement } = windowScroll
        ? windowVirtualizer
        : virtualizer;
    const totalSize = getTotalSize();
    const virtualRows = getVirtualItems();
    const virtualRowsLength = virtualRows.length;
    // Without these paddings, the container would shrink or expand with the mounting and unmounting of rows, making the scrollbar erratic
    const paddingTop = virtualRowsLength > 0
        ? paddingTopCalc({
            row: virtualRows[0],
            offset: parentOffsetRef.current,
        })
        : 0;
    const paddingBottom = virtualRowsLength > 0
        ? paddingBottomCalc({
            row: virtualRows[virtualRowsLength - 1],
            totalSize,
        })
        : 0;
    useEffect(() => {
        if (flagFirstRenderRef.current) {
            const selectedRows = table.getSelectedRowModel();
            const originalSelectedRows = selectedRows.rows.map((row) => row.original);
            onRowSelectionChange === null || onRowSelectionChange === void 0 ? void 0 : onRowSelectionChange({
                selectedRowIndexes: rowSelection,
                selectedRows: originalSelectedRows,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onRowSelectionChange, rowSelection]);
    useEffect(() => {
        flagFirstRenderRef.current = true;
    }, []);
    return (_jsx(Box, Object.assign({ height: windowScroll ? totalSize : "100%", width: "100%" }, { children: _jsxs(TableContainer, Object.assign({ ref: tableContainerRef, sx: Object.assign(Object.assign({}, styles.tableContainer), (windowScroll
                ? {}
                : {
                    overflowY: "auto",
                    height: tableMaxHeight ? "auto" : tableHeight,
                    maxHeight: tableMaxHeight,
                })), onScroll: onScroll }, { children: [_jsxs(Table, Object.assign({ sx: Object.assign({}, styles.table) }, { children: [_jsx(Thead, Object.assign({ sx: styles.thead }, { children: table.getHeaderGroups().map((headerGroup) => (_jsx(Tr, { children: headerGroup.headers.map((header) => header.isPlaceholder ? null : (_jsx(Th, Object.assign({ width: header.getSize() === Number.MAX_SAFE_INTEGER
                                        ? "auto"
                                        : `${header.getSize()}px`, sx: Object.assign(Object.assign({}, styles.th), { cursor: header.column.getCanSort()
                                            ? "pointer"
                                            : undefined, userSelect: header.column.getCanSort()
                                            ? "none"
                                            : undefined }), style: Object.assign({}, getCommonPinningStyles(header.column, false)), onClick: () => {
                                        if (header.column.getCanSort()) {
                                            header.column.toggleSorting();
                                        }
                                    }, className: `datatable-th-${header.id}` }, { children: _jsxs(Flex, Object.assign({ display: "flex", alignItems: "center", 
                                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                        // @ts-ignore
                                        justifyContent: header.column.columnDef.align }, { children: [flexRender(header.column.columnDef.header, header.getContext()), header.column.getCanSort() ? (_jsx(Flex, Object.assign({ sx: styles.sortIconsContainer }, { children: _jsx(SvgSpriteConsumer, { svgId: getSortIcon(header.column.getIsSorted()), size: 24 }) }))) : null] })) }), header.id))) }, headerGroup.id))) })), _jsxs(Tbody, Object.assign({ sx: styles.tbody }, { children: [_jsx(CustomTr, { padding: paddingTop }), virtualRows.map((virtualRow) => {
                                    const { index, size } = virtualRow;
                                    const row = table.getRowModel().rows[index];
                                    // if row exist then the current virtual row has data linked
                                    // and is not a "fakeline" created for skeleton
                                    if (row) {
                                        const { id, getIsSelected, getVisibleCells } = row;
                                        const focus = isFocused ? isFocused(row) : false;
                                        return (_jsx(Tr, Object.assign({ ref: measureElement, "data-index": index, sx: Object.assign(Object.assign({}, styles.tr), (onClickRow
                                                ? {
                                                    cursor: "pointer",
                                                    _hover: {
                                                        td: {
                                                            background: "semantic.cta.secondaryHover",
                                                        },
                                                    },
                                                }
                                                : {})), height: `${size}px`, className: `ds-row-${index % 2 === 0 ? "even" : "odd"} ${getIsSelected() ? "ds-selected-row" : ""}`, onClick: () => {
                                                if (onClickRow) {
                                                    onClickRow({ index, row: row.original });
                                                }
                                            }, onKeyDown: (e) => {
                                                if (onClickRow) {
                                                    if ((e.key === KeyboardCodes.ENTER ||
                                                        e.key === KeyboardCodes.SPACE) &&
                                                        e.target.tagName === "TR") {
                                                        e.preventDefault();
                                                        onClickRow({ index, row: row.original });
                                                    }
                                                }
                                            } }, (onClickRow && {
                                            role: "button",
                                            tabIndex: 0,
                                            "aria-label": rowClickLabel,
                                        }), { children: getVisibleCells().map((cell, cellIndex) => (_jsx(CustomCell, { cell: cell, isFocused: focus }, cellIndex))) }), id));
                                    }
                                    // if there's no row its a fake line
                                    return (_jsxs(Tr, { children: [enableRowSelection && (_jsx(Td, Object.assign({ sx: styles.td }, { children: _jsx(Skeleton, { height: "16px", type: SkeletonTypes.RECTANGLE, width: "100%" }) }), `${virtualRow.key}-rowSelection`)), columns
                                                .filter((col) => columnVisibility
                                                ? // @ts-expect-error column have ids avoiding breaking change in type=
                                                    columnVisibility[col.id] === true
                                                : true)
                                                .map((col) => (
                                            // @ts-expect-error column have ids avoiding breaking change in type
                                            _jsx(Td, Object.assign({ sx: styles.td }, { children: _jsx(Skeleton, { height: "16px", type: SkeletonTypes.RECTANGLE, width: "100%" }) }), `${virtualRow.key}-${col.id}`)))] }, virtualRow.key));
                                }), _jsx(CustomTr, { padding: paddingBottom })] }))] })), !(data === null || data === void 0 ? void 0 : data.length) && !hasNextPage ? (_jsx(Flex, Object.assign({ sx: styles.notFoundContainer }, { children: _jsxs(Flex, Object.assign({ direction: "column", alignItems: "center" }, { children: [_jsx(Illustration, { width: 140, height: 140, illustrationId: noResultIllustrationId, cdnUrl: `${PROD_CDN_BASE_URL}/illustrations/`, alt: "No Data" }), _jsx(Text, Object.assign({ variant: "body.largeBold" }, { children: noResultLabel }))] })) }))) : null] })) })));
};
InfiniteDataTable.displayName = "InfiniteDataTable";
export default forwardRef(InfiniteDataTable);
//# sourceMappingURL=InfiniteDataTable.js.map